import React, { FC, useMemo } from 'react';

import BasicButton from '../../../components/buttons/basicButton';
import BasicModal from '../../../components/modals/basicModal';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

import ComposerNXFeatureBlock from './ComposerNXFeatureBlock';

interface ComposerNXIntroModalProps {
    handleGoBack: () => void;
    handleOpenAampeNX: () => void;
}

const ComposerNXIntroModal: FC<
    React.PropsWithChildren<ComposerNXIntroModalProps>
> = ({ handleGoBack, handleOpenAampeNX }) => {
    const iconStyle = useMemo(
        () => ({ color: aampeTheme.colors.solidA01 }),
        [],
    );

    const availableFeatures = useMemo(
        () => [
            {
                icon: <ICONS.circleCheck style={iconStyle} />,
                title: 'Mobile Push Messages',
                description:
                    'Create mobile push messages with components, labels and alternates',
            },
            {
                icon: <ICONS.circleCheck style={iconStyle} />,
                title: 'Backward Compatibility',
                description:
                    'Messages created in the new composer will also appear in the old composer',
            },
        ],
        [iconStyle],
    );

    const comingSoonFeatures = useMemo(
        () => [
            {
                icon: <ICONS.clock style={iconStyle} />,
                title: 'CMS & Datasets',
                description:
                    'Add datasets and CMS to your mobile push messages',
            },
            {
                icon: <ICONS.clock style={iconStyle} />,
                title: 'Other Channels',
                description:
                    'Support for email messages to co-exist with push notifications',
            },
        ],
        [iconStyle],
    );

    return (
        <BasicModal
            childContainerStyle={{ padding: 0 }}
            containerBodyStyle={{
                borderRadius: aampeTheme.spacing4(3),
                overflow: 'hidden',
            }}
            width={aampeTheme.spacing4(175)} // 700px
        >
            <ModalContent>
                {/* Header Section */}
                <HeaderSection>
                    <HeaderTitle>Before you Kickoff</HeaderTitle>
                    <HeaderSubtitle>
                        Composer 2.0 is still in beta release with limited
                        functionality.
                    </HeaderSubtitle>
                </HeaderSection>

                <MainContent>
                    <Column>
                        <ComposerNXFeatureBlock
                            sectionTitle="CURRENTLY AVAILABLE"
                            features={availableFeatures}
                        />
                    </Column>
                    <Column>
                        <ComposerNXFeatureBlock
                            sectionTitle="COMING SOON!"
                            features={comingSoonFeatures}
                        />
                    </Column>
                </MainContent>

                <FooterSection>
                    <StyledButton
                        name="Cancel"
                        isPrimary={false}
                        action={handleGoBack}
                    />
                    <StyledButton
                        name="Open Aampe 2.0 ↗"
                        isPrimary={true}
                        action={handleOpenAampeNX}
                    />
                </FooterSection>
            </ModalContent>
        </BasicModal>
    );
};

const ModalContent = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    padding: aampeTheme.spacing4(6),
    gap: aampeTheme.spacing4(4),
    minHeight: aampeTheme.spacing4(100),
    backgroundColor: aampeTheme.colors.white,
}));

const HeaderSection = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(1.5),
    alignItems: 'flex-start',
}));

const HeaderTitle = styled.h2(() => ({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.title,
    fontWeight: aampeTheme.weights.heavy,
    lineHeight: 'normal',
    margin: 0,
}));

const HeaderSubtitle = styled.p(() => ({
    color: aampeTheme.colors.composerNXText,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    lineHeight: '150%',
    margin: 0,
}));

const MainContent = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing4(4),
}));

const Column = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

const FooterSection = styled.div(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: aampeTheme.spacing4(3),
}));

const StyledButton = styled(BasicButton)<{ isPrimary: boolean }>(
    ({ isPrimary }) => ({
        height: aampeTheme.spacing4(8),
        minWidth: 'auto',
        padding: `0 ${aampeTheme.spacing4(2.5)}`,
        backgroundColor: isPrimary ? aampeTheme.colors.solidA01 : 'transparent',
        borderColor: aampeTheme.colors.solidA01,
        color: isPrimary ? undefined : aampeTheme.colors.solidA01,
        fontSize: aampeTheme.size.font.textMedium,
        fontWeight: aampeTheme.weights.medium,
        '&:focus-visible': {
            outline: `2px solid ${aampeTheme.colors.solidA01}`,
        },
    }),
);

export default ComposerNXIntroModal;
